import { createFeature, createReducer, on } from '@ngrx/store'

import { DepartmentState } from './department.state'
import {
  blockDepartment,
  blockDepartmentSuccess,
  createDepartment,
  createDepartmentFailure,
  createDepartmentSuccess,
  deleteDepartment,
  deleteDepartmentFailure,
  deleteDepartmentSuccess,
  loadDepartments,
  loadDepartmentsFailure,
  loadDepartmentsSuccess,
  resetDepartmentState,
  unblockDepartment,
  unblockDepartmentSuccess,
  updateDepartment,
  updateDepartmentFailure,
  updateDepartmentSuccess
} from './department.actions'

export const initialState: DepartmentState = {
  totalCount: 0,
  departments: [],
  loading: false,
  error: null
}

// Create the Department reducer
export const departmentReducer = createReducer(
  initialState,
  on(loadDepartments, state => ({
    ...state,
    loading: true,
    error: null
  })),
  on(loadDepartmentsSuccess, (state, { totalCount, departments }) => ({
    ...state,
    totalCount,
    departments,
    loading: false,
    error: null
  })),
  on(loadDepartmentsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),
  on(createDepartment, state => ({ ...state, loading: true })),
  on(createDepartmentSuccess, (state, { department }) => ({
    ...state,
    departments: [...state.departments, department],
    totalCount: state.totalCount + 1,
    loading: false
  })),
  on(createDepartmentFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),
  on(updateDepartment, state => ({ ...state, loading: true })),
  on(updateDepartmentSuccess, (state, { department }) => ({
    ...state,
    department,
    loading: false
  })),
  on(updateDepartmentFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),
  on(blockDepartment, (state, { id }) => ({
    ...state,
    departments: state.departments.map(department =>
      String(department.id) === id ? { ...department, disabled: true } : department
    )
  })),
  on(blockDepartmentSuccess, (state, { department }) => {
    // Update the business state to reflect that it is blocked
    const updatedState = {
      ...state,
      departments: state.departments.map(
        b => {
          if (b.id === department.id) {
            // console.log('updated business', { ...b, ...department })
            return { ...b, ...department }
          } else return b
        } // Update the business object
      ),
      error: null
    }
    return updatedState
  }),
  on(unblockDepartment, (state, { id }) => ({
    ...state,
    departments: state.departments.map(department =>
      String(department.id) === id ? { ...department, disabled: false } : department
    )
  })),
  on(unblockDepartmentSuccess, (state, { id }) => {
    // Update the business state to reflect that it is unblocked
    return {
      ...state,
      departments: state.departments.map(department =>
        String(department.id) === id ? { ...department, disabled: false } : department
      ),
      error: null
    }
  }),
  on(deleteDepartment, (state, { id }) => ({
    ...state,
    departments: state.departments.map(department =>
      String(department.id) === id ? { ...department, disabled: true } : department
    )
  })),
  on(deleteDepartmentSuccess, (state, { id }) => {
    // Update the business state to reflect that it is successfully deleted
    const updatedState = {
      ...state,
      departments: state.departments.filter(b => b.id !== id), // Remove the business from the list
      error: null,
      totalCount: state.totalCount - 1
    }
    return updatedState
  }),
  on(deleteDepartmentFailure, (state, { error }) => ({
    ...state,
    error
  })),
  on(resetDepartmentState, () => initialState)
)

// Create the feature for the Department state
export const departmentFeature = createFeature({
  name: 'department', // Feature name
  reducer: departmentReducer
})
export type { DepartmentState }
// Export the feature's selectors
export const { name, reducer, selectDepartments, selectLoading, selectError } = departmentFeature
